export const getDefaultProfileImage = () => require("assets/images/staff-default-rounded.png");

/** Sendbird group channel custom types used for team (non-patient) messenger. */
const TEAM_MESSENGER_CUSTOM_TYPES = new Set(["internal", "external"]);

export const isTeamMessengerChannel = (channel) =>
  TEAM_MESSENGER_CUSTOM_TYPES.has(
    channel?.customType || channel?.custom_type || "",
  );

/**
 * Resolves a Sendbird group channel URL from SDK or FCM payload shapes.
 * @param {object|null|undefined} channel
 * @returns {string}
 */
export const getChannelUrl = (channel) =>
  channel?.url || channel?._url || channel?.channel_url || channel?.channelUrl || "";

/**
 * @param {object|null|undefined} channel
 * @param {string} chatUrl
 * @returns {boolean}
 */
export const channelMatchesUrl = (channel, chatUrl) => {
  if (!channel || !chatUrl) return false;
  const url = getChannelUrl(channel);
  if (!url) return false;
  if (url === chatUrl) return true;
  try {
    return url === decodeURIComponent(chatUrl);
  } catch {
    return false;
  }
};

/**
 * Resolves staff designation id(s) stored on a channel `data.members[]` entry.
 * Supports `designationIds` (preferred) or legacy `designationId`.
 * @param {Record<string, unknown>} member
 * @returns {number[]}
 */
export const getDesignationIdsFromChannelMemberRecord = (member) => {
  if (!member) return [];
  if (Array.isArray(member.designationIds) && member.designationIds.length) {
    return [
      ...new Set(
        member.designationIds
          .map((id) => Number(id))
          .filter((n) => !Number.isNaN(n)),
      ),
    ];
  }
  const raw =
    member.designationId ??
    member.designation?.id ??
    member.designation?.designationId;
  if (raw != null && raw !== "") {
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
  }
  return [];
};

/**
 * Resolves office id(s) stored on a channel `data.members[]` entry.
 * New channels include `officeIds`; legacy rows only have `officeId`.
 * @param {Record<string, unknown>} member
 * @returns {number[]}
 */
export const getOfficeIdsFromChannelMemberRecord = (member) => {
  if (!member) return [];
  if (Array.isArray(member.officeIds) && member.officeIds.length) {
    return [
      ...new Set(
        member.officeIds
          .map((id) => Number(id))
          .filter((n) => !Number.isNaN(n)),
      ),
    ];
  }
  const id = member.officeId;
  if (id != null && id !== "") {
    const n = Number(id);
    return Number.isNaN(n) ? [] : [n];
  }
  return [];
};

export const parseChannelMembers = (rawData) => {
  if (!rawData) return [];
  try {
    return JSON.parse(rawData)?.members || [];
  } catch {
    return [];
  }
};

/**
 * Sendbird `channel.memberCount` can lag after custom `data.members` is updated (e.g. kick).
 * Prefer counts from custom payload and/or live `channel.members`.
 * @param {number} memberCount
 * @param {unknown[]|undefined} members Sendbird channel.members
 * @param {unknown[]|undefined} customMembers Parsed + attached list for UI
 * @returns {number}
 */
export const resolveMessengerGroupMemberCount = (
  memberCount,
  members,
  customMembers,
) => {
  if (memberCount <= 2) return memberCount;
  const countFromCustom = Array.isArray(customMembers)
    ? customMembers.length
    : 0;
  const countFromSdk = Array.isArray(members) ? members.length : 0;
  if (countFromCustom > 0 && countFromSdk > 0) {
    return Math.min(countFromCustom, countFromSdk);
  }
  if (countFromCustom > 0) return countFromCustom;
  if (countFromSdk > 0) return countFromSdk;
  return memberCount;
};

export const getOtherChannelMembers = (members = [], currentUserId) => {
  const normalizedId = currentUserId?.toString();
  return members.filter((mem) => mem?.id?.toString() !== normalizedId);
};

export const getOtherSendbirdMembers = (members = [], currentUserId) => {
  const normalizedId = currentUserId?.toString();
  return members.filter((mem) => mem?.userId?.toString() !== normalizedId);
};

export const attachProfilePicFromChannelMembers = (
  customMembers = [],
  channelMembers = [],
) => {
  return customMembers.flatMap((member) => {
    const matchedMember = channelMembers.find(
      (channelMember) => channelMember?.userId?.toString() === member?.id?.toString(),
    );
    if(!matchedMember) return [];
    return {
      ...member,
      profilePic: matchedMember?.plainProfileUrl,
      coverImage: matchedMember?.plainProfileUrl,
    };
  });
};

